// ===== Terminal store: symbols, tick engine, candles, trading, persistence =====
import { useSyncExternalStore } from "react";
import { rsi, sma, ema } from "./indicators";

export const TF_LIST = ["M1", "M5", "M15", "M30", "H1", "H4", "D1", "W1", "MN"];
export const TF_MS = { M1: 6e4, M5: 3e5, M15: 9e5, M30: 18e5, H1: 36e5, H4: 144e5, D1: 864e5, W1: 6048e5, MN: 2592e6 };
// Bar open time: weeks start Monday 00:00 UTC, months on the 1st, others epoch-aligned
export const barStart = (tf, t) => {
  if (tf === "W1") { const d = Math.floor(t / 864e5); return (d - ((d + 3) % 7)) * 864e5; }
  if (tf === "MN") { const d = new Date(t); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1); }
  return Math.floor(t / TF_MS[tf]) * TF_MS[tf];
};
const TICK_MS = 500;
const LS_KEY = "mt5web_v1";

// [name, price, digits, spreadPts, volatility/tick, contract, description]
const SYMBOL_DEFS = [
  ["EURUSD", 1.0852, 5, 12, 0.00006, 1e5, "Euro vs US Dollar"],
  ["GBPUSD", 1.2714, 5, 15, 0.00007, 1e5, "Great Britain Pound vs US Dollar"],
  ["USDJPY", 151.42, 3, 14, 0.00006, 1e5, "US Dollar vs Japanese Yen"],
  ["USDCHF", 0.8821, 5, 16, 0.00006, 1e5, "US Dollar vs Swiss Franc"],
  ["AUDUSD", 0.6584, 5, 14, 0.00007, 1e5, "Australian Dollar vs US Dollar"],
  ["USDCAD", 1.3562, 5, 18, 0.00006, 1e5, "US Dollar vs Canadian Dollar"],
  ["NZDUSD", 0.6091, 5, 20, 0.00007, 1e5, "New Zealand Dollar vs US Dollar"],
  ["EURGBP", 0.8535, 5, 17, 0.00005, 1e5, "Euro vs Great Britain Pound"],
  ["EURJPY", 164.32, 3, 21, 0.00007, 1e5, "Euro vs Japanese Yen"],
  ["GBPJPY", 192.54, 3, 28, 0.00008, 1e5, "Great Britain Pound vs Japanese Yen"],
  ["EURCHF", 0.9573, 5, 22, 0.00005, 1e5, "Euro vs Swiss Franc"],
  ["AUDJPY", 99.71, 3, 22, 0.00008, 1e5, "Australian Dollar vs Japanese Yen"],
  ["EURAUD", 1.6482, 5, 26, 0.00007, 1e5, "Euro vs Australian Dollar"],
  ["XAUUSD", 2341.25, 2, 30, 0.00009, 100, "Gold vs US Dollar"],
  ["XAGUSD", 27.845, 3, 35, 0.00012, 5000, "Silver vs US Dollar"],
  ["BTCUSD", 67250.5, 2, 1500, 0.0002, 1, "Bitcoin vs US Dollar"],
  ["ETHUSD", 3480.25, 2, 150, 0.00025, 1, "Ethereum vs US Dollar"],
  ["LTCUSD", 84.52, 2, 8, 0.0003, 1, "Litecoin vs US Dollar"],
  ["XRPUSD", 0.52341, 5, 60, 0.0003, 1, "Ripple vs US Dollar"],
  ["SOLUSD", 152.34, 2, 12, 0.0003, 1, "Solana vs US Dollar"],
  ["US30", 39120.5, 1, 20, 0.00008, 1, "Dow Jones Industrial Average"],
  ["NAS100", 18240.3, 1, 15, 0.0001, 1, "Nasdaq 100 Index"],
  ["GER40", 18350.2, 1, 12, 0.00008, 1, "Germany 40 Index"],
  ["USOIL", 78.42, 2, 4, 0.00012, 1000, "Crude Oil WTI"],
];

const gauss = () => {
  let u = 0, v = 0;
  while (!u) u = Math.random();
  while (!v) v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
};
export const round = (v, d) => Math.round(v * 10 ** d) / 10 ** d;
const pad = (n, l = 2) => String(n).padStart(l, "0");
export const fmtTime = (ms, sec = false) => {
  const d = new Date(ms);
  const s = `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return sec ? `${s}:${pad(d.getSeconds())}` : s;
};
export const fmtMoney = (v) => {
  const [i, f] = Math.abs(v).toFixed(2).split(".");
  return `${v < 0 ? "-" : ""}${i.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}.${f}`;
};

// ----- Initial state -----
const symbols = {};
SYMBOL_DEFS.forEach(([name, price, digits, spread, vol, contract, desc]) => {
  const point = 10 ** -digits;
  const open = round(price * (1 + gauss() * 0.003), digits);
  symbols[name] = {
    name, digits, point, spread, vol, contract, desc, base: price, half: (spread * point) / 2,
    bid: price, ask: round(price + spread * point, digits),
    dir: 0, open, high: Math.max(open, price), low: Math.min(open, price),
    ticks: [], tickTime: Date.now(),
  };
});

let saved = {};
try { saved = JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch (e) { saved = {}; }

let chartSeq = 3;
const mkChart = (id, symbol, tf) => ({
  id, symbol, tf, type: "candles", grid: true, volumes: false, oneClick: true, autoScroll: true,
  indicators: [], drawings: [],
});

const state = {
  symbols,
  watch: saved.watch || SYMBOL_DEFS.map((d) => d[0]),
  charts: saved.charts || [mkChart(1, "EURUSD", "H1"), mkChart(2, "XAUUSD", "M15"), mkChart(3, "BTCUSD", "H4")],
  activeChart: saved.activeChart || 1,
  tool: "cursor",
  theme: saved.theme || "dark",
  tile: saved.tile || false,
  liveFeed: saved.liveFeed ?? true,
  feedStatus: "off",
  marketStatus: "off",
  popups: [],
  dom: null,
  algo: saved.algo ?? true,
  account: { login: 51842907, server: "WebTrade-Demo", name: "Demo Account", balance: saved.balance ?? 10000, currency: "USD", leverage: 100 },
  positions: saved.positions || [],
  orders: saved.orders || [],
  history: saved.history || [],
  alerts: saved.alerts || [],
  journal: [],
  experts: [],
  ticket: saved.ticket || 50120001,
  panels: { market: true, navigator: true, toolbox: true },
  dialog: null,
  menu: null,
  version: 0,
};
if (saved.charts) chartSeq = Math.max(...saved.charts.map((c) => c.id), 3);

// ----- Pub/sub -----
const listeners = new Set();
let pending = false;
export const emit = () => {
  state.version++;
  if (pending) return;
  pending = true;
  queueMicrotask(() => { pending = false; listeners.forEach((l) => l()); });
};
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
export const getState = () => state;
export const useStore = () => { useSyncExternalStore(subscribe, () => state.version); return state; };
export const onChange = subscribe;

const persist = () => {
  const { watch, charts, activeChart, theme, tile, liveFeed, algo, positions, orders, history, alerts, ticket } = state;
  localStorage.setItem(LS_KEY, JSON.stringify({ watch, charts, activeChart, theme, tile, liveFeed, algo, positions, orders, history, alerts, ticket, balance: state.account.balance }));
};
export const update = (fn) => { fn(state); persist(); emit(); };

// ----- Logging -----
export const log = (message, source = "Trades", where = "journal") => {
  state[where].unshift({ id: Math.random(), time: Date.now(), source, message });
  if (state[where].length > 300) state[where].pop();
  emit();
};

// ----- Candle series (generated backwards from current price, then updated by ticks) -----
const series = {};
export const hooks = { seriesCreated: null, marketSeries: null };
export const symbolOf = (n) => symbols[n];
export const getSeries = (sym, tf) => {
  const key = `${sym}|${tf}`;
  if (series[key]) return series[key];
  const s = symbols[sym];
  const ms = TF_MS[tf];
  const lastStart = barStart(tf, Date.now());
  const lastD = new Date(lastStart);
  const sigma = Math.min(0.03, s.vol * Math.sqrt(ms / TICK_MS) * 0.35);
  let c = s.bid;
  const bars = [];
  const count = tf === "MN" ? 240 : tf === "W1" ? 520 : 1200;
  for (let i = 0; i < count; i++) {
    // walking backwards, pull each open gently toward the base price so history stays bounded
    const o = round(c * (1 + ((s.base - c) / s.base) * 0.02 + gauss() * sigma), s.digits);
    const h = round(Math.max(o, c) * (1 + Math.abs(gauss()) * sigma * 0.45), s.digits);
    const l = round(Math.min(o, c) * (1 - Math.abs(gauss()) * sigma * 0.45), s.digits);
    bars.push({ t: tf === "MN" ? Date.UTC(lastD.getUTCFullYear(), lastD.getUTCMonth() - i, 1) : lastStart - i * ms, o, h, l, c, v: Math.round(200 + Math.random() * 1800 * Math.sqrt(ms / 6e4)) });
    c = o;
  }
  bars.reverse();
  bars[bars.length - 1].v = 1;
  series[key] = bars;
  if (hooks.seriesCreated) hooks.seriesCreated(sym, tf);
  if (hooks.marketSeries) hooks.marketSeries(sym, tf);
  return bars;
};

// ----- Profit / margin math -----
const toUSD = (sym, v) => {
  if (sym.length !== 6 || sym.endsWith("USD")) return v;
  const q = sym.slice(3);
  if (sym.startsWith("USD")) return v / symbols[sym].bid;
  const rates = { JPY: 1 / symbols.USDJPY.bid, GBP: symbols.GBPUSD.bid, AUD: symbols.AUDUSD.bid, CHF: 1 / symbols.USDCHF.bid, CAD: 1 / symbols.USDCAD.bid };
  return v * (rates[q] || 1);
};
export const posPrice = (p) => (p.type === "buy" ? symbols[p.symbol].bid : symbols[p.symbol].ask);
export const posProfit = (p) => {
  const s = symbols[p.symbol];
  const diff = (posPrice(p) - p.price) * (p.type === "buy" ? 1 : -1);
  return round(toUSD(p.symbol, diff * p.volume * s.contract), 2);
};
const posMargin = (p) => {
  const s = symbols[p.symbol];
  const notional = p.volume * s.contract * p.price;
  const usd = p.symbol.startsWith("USD") && p.symbol.length === 6 ? p.volume * s.contract : toUSD(p.symbol, notional);
  return usd / state.account.leverage;
};
export const accountInfo = () => {
  const profit = state.positions.reduce((a, p) => a + posProfit(p) + p.swap, 0);
  const margin = state.positions.reduce((a, p) => a + posMargin(p), 0);
  const equity = state.account.balance + profit;
  return { balance: state.account.balance, equity, margin, free: equity - margin, level: margin ? (equity / margin) * 100 : 0, profit };
};

// ----- Trading actions -----
const typeName = { buy: "buy", sell: "sell", buy_limit: "buy limit", sell_limit: "sell limit", buy_stop: "buy stop", sell_stop: "sell stop" };
export const openPosition = (symbol, type, volume, sl = 0, tp = 0, comment = "") => {
  const s = symbols[symbol];
  const price = type === "buy" ? s.ask : s.bid;
  const p = { ticket: ++state.ticket, symbol, type, volume: +volume, price, sl: +sl || 0, tp: +tp || 0, time: Date.now(), swap: 0, comment };
  state.positions.unshift(p);
  log(`market ${type} ${p.volume} ${symbol}${p.sl ? ` sl: ${p.sl}` : ""}${p.tp ? ` tp: ${p.tp}` : ""}`);
  log(`deal #${p.ticket} ${type} ${p.volume} ${symbol} at ${price.toFixed(s.digits)} done`);
  update(() => {});
  return p;
};
export const closePosition = (ticket, reason = "") => {
  const p = state.positions.find((x) => x.ticket === ticket);
  if (!p) return;
  const profit = posProfit(p);
  state.positions = state.positions.filter((x) => x.ticket !== ticket);
  state.account.balance = round(state.account.balance + profit + p.swap, 2);
  state.history.unshift({ ...p, closeTime: Date.now(), closePrice: posPrice(p), profit });
  log(`${reason ? reason + " " : ""}close #${ticket} ${p.type} ${p.volume} ${p.symbol} at ${posPrice(p).toFixed(symbols[p.symbol].digits)}, profit ${profit.toFixed(2)}`);
  update(() => {});
};
export const modifyPosition = (ticket, sl, tp) => update((st) => {
  const p = st.positions.find((x) => x.ticket === ticket) || st.orders.find((x) => x.ticket === ticket);
  if (!p) return;
  p.sl = +sl || 0; p.tp = +tp || 0;
  log(`modify #${ticket} ${p.symbol} sl: ${p.sl}, tp: ${p.tp}`);
});
export const placeOrder = (symbol, type, volume, price, sl, tp, comment = "") => {
  const o = { ticket: ++state.ticket, symbol, type, volume: +volume, price: +price, sl: +sl || 0, tp: +tp || 0, time: Date.now(), comment };
  state.orders.unshift(o);
  log(`${typeName[type]} ${o.volume} ${symbol} at ${o.price} placed #${o.ticket}`);
  update(() => {});
};
export const cancelOrder = (ticket) => update((st) => {
  st.orders = st.orders.filter((o) => o.ticket !== ticket);
  log(`order #${ticket} canceled`);
});

// ----- Chart actions -----
export const chartOf = (id = state.activeChart) => state.charts.find((c) => c.id === id) || state.charts[0];
export const setChart = (patch, id) => update(() => Object.assign(chartOf(id), patch));
export const newChart = (symbol, tf = "H1") => update((st) => {
  const c = mkChart(++chartSeq, symbol, tf);
  st.charts.push(c);
  st.activeChart = c.id;
});
export const closeChart = (id) => update((st) => {
  if (st.charts.length === 1) return;
  st.charts = st.charts.filter((c) => c.id !== id);
  if (st.activeChart === id) st.activeChart = st.charts[st.charts.length - 1].id;
});
export const IND_DEFAULTS = {
  SMA: { period: 20, color: "#FFD600" }, EMA: { period: 50, color: "#FF6D00" },
  BB: { period: 20, dev: 2, color: "#26C6DA" }, RSI: { period: 14, color: "#2196F3" },
  MACD: { fast: 12, slow: 26, signal: 9, color: "#B0BEC5" }, STOCH: { k: 5, d: 3, slow: 3, color: "#00BCD4" },
};
export const addIndicator = (type, id) => update(() => {
  const c = chartOf(id);
  c.indicators.push({ id: Math.random().toString(36).slice(2), type, ...IND_DEFAULTS[type] });
  log(`${c.symbol},${c.tf}: indicator ${type} loaded successfully`, "Indicators", "experts");
});
export const setUI = (patch) => { Object.assign(state, patch); emit(); };

// ----- Tick engine -----
const checkTriggers = () => {
  [...state.positions].forEach((p) => {
    const px = posPrice(p);
    const isBuy = p.type === "buy";
    if (p.sl && (isBuy ? px <= p.sl : px >= p.sl)) closePosition(p.ticket, "stop loss triggered");
    else if (p.tp && (isBuy ? px >= p.tp : px <= p.tp)) closePosition(p.ticket, "take profit triggered");
  });
  [...state.orders].forEach((o) => {
    const s = symbols[o.symbol];
    const hit = { buy_limit: s.ask <= o.price, sell_limit: s.bid >= o.price, buy_stop: s.ask >= o.price, sell_stop: s.bid <= o.price }[o.type];
    if (!hit) return;
    state.orders = state.orders.filter((x) => x.ticket !== o.ticket);
    log(`order #${o.ticket} ${typeName[o.type]} triggered`);
    openPosition(o.symbol, o.type.startsWith("buy") ? "buy" : "sell", o.volume, o.sl, o.tp, o.comment);
  });
};

// Applies a new bid/ask to a symbol and updates every cached candle series
const applyQuote = (s, bid, ask, now) => {
  const prev = s.bid;
  s.bid = round(bid, s.digits);
  s.ask = round(ask, s.digits);
  s.dir = s.bid > prev ? 1 : s.bid < prev ? -1 : 0;
  s.high = Math.max(s.high, s.bid); s.low = Math.min(s.low, s.bid);
  s.tickTime = now;
  s.ticks.push({ t: now, bid: s.bid, ask: s.ask });
  if (s.ticks.length > 150) s.ticks.shift();
  TF_LIST.forEach((tf) => {
    const bars = series[`${s.name}|${tf}`];
    if (!bars) return;
    const start = barStart(tf, now);
    const last = bars[bars.length - 1];
    if (start > last.t) bars.push({ t: start, o: s.bid, h: s.bid, l: s.bid, c: s.bid, v: 1 });
    else { last.c = s.bid; last.h = Math.max(last.h, s.bid); last.l = Math.min(last.l, s.bid); last.v++; }
  });
};

// ----- Live feed bridge (Binance) -----
export const CRYPTO = { BTCUSD: "BTCUSDT", ETHUSD: "ETHUSDT", LTCUSD: "LTCUSDT", XRPUSD: "XRPUSDT", SOLUSD: "SOLUSDT" };
// Rescales synthetic history so it ends at the first real price
const rescale = (s, bid) => {
  const ratio = bid / s.bid;
  TF_LIST.forEach((tf) => {
    const bars = series[`${s.name}|${tf}`];
    if (!bars || bars.real) return;
    bars.forEach((b) => { b.o = round(b.o * ratio, s.digits); b.h = round(b.h * ratio, s.digits); b.l = round(b.l * ratio, s.digits); b.c = round(b.c * ratio, s.digits); });
  });
  s.open = round(s.open * ratio, s.digits); s.high = bid; s.low = bid; s.base = bid; s.ticks = [];
  s.bid = bid;
};
export const setLiveQuote = (name, bid, ask) => {
  const s = symbols[name];
  if (!s.live) { rescale(s, bid); s.live = true; }
  s.liveQ = { bid, ask };
};
export const setLiveOpen = (name, open, high, low) => {
  const s = symbols[name];
  if (!s.live) return;
  s.open = open; s.high = high; s.low = low;
};
export const setRealBars = (name, tf, bars) => {
  const s = symbols[name];
  bars.real = true;
  series[`${name}|${tf}`] = bars;
  const c = bars[bars.length - 1].c;
  if (!s.live && CRYPTO[name]) setLiveQuote(name, c, c + s.spread * s.point);
  emit();
};

// ----- Real non-crypto quotes (Yahoo via backend); mid price -> bid/ask with the symbol's spread -----
export const MARKET = { EURUSD: 1, GBPUSD: 1, USDJPY: 1, USDCHF: 1, AUDUSD: 1, USDCAD: 1, NZDUSD: 1, EURGBP: 1, EURJPY: 1, GBPJPY: 1, EURCHF: 1, AUDJPY: 1, EURAUD: 1, XAUUSD: 1, XAGUSD: 1, US30: 1, NAS100: 1, GER40: 1, USOIL: 1 };
export const setMarketQuote = (name, price, prev, time) => {
  const s = symbols[name];
  if (!s || !MARKET[name]) return;
  const bid = round(price - s.half, s.digits);
  if (!s.anchored) { rescale(s, bid); s.anchored = true; s.snap = bid; }
  else if (Math.abs(bid - s.base) > s.point / 2) s.snap = bid;
  s.base = bid;
  if (prev) s.open = round(prev - s.half, s.digits);
  s.closed = Date.now() - time * 1000 > 15 * 60e3;
};
export const stopLive = () => Object.values(symbols).filter((s) => CRYPTO[s.name]).forEach((s) => { s.live = false; s.liveQ = null; s.base = s.bid; });

// ----- Price alerts -----
export const ALERT_CONDS = ["Bid >", "Bid <", "Ask >", "Ask <"];
let audioCtx = null;
export const beep = () => {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    [880, 660, 880].forEach((f, i) => {
      const o = audioCtx.createOscillator(), g = audioCtx.createGain();
      o.frequency.value = f; o.type = "square"; g.gain.value = 0.06;
      o.connect(g); g.connect(audioCtx.destination);
      const t = audioCtx.currentTime + i * 0.16;
      o.start(t); o.stop(t + 0.12);
    });
  } catch (e) { /* audio unavailable */ }
};
// Indicator alert helpers: RSI vs level, fast MA vs slow MA on the latest (live) bar
export const CROSS_CONDS = ["crosses above", "crosses below", "crosses"];
const lastOf = (arr) => arr[arr.length - 1];
export const alertValue = (a) => {
  const close = getSeries(a.symbol, a.tf).slice(-600).map((b) => b.c);
  if (a.kind === "rsi") return { v: lastOf(rsi(close, +a.period)), ref: +a.level };
  const f = a.maType === "EMA" ? ema : sma;
  return { v: lastOf(f(close, +a.fast)), ref: lastOf(f(close, +a.slow)) };
};
export const describeAlert = (a) => {
  if (a.kind === "rsi") return `RSI(${a.period}) ${a.tf} ${a.cross} ${a.level}`;
  if (a.kind === "ma") return `${a.maType}(${a.fast}) ${a.cross} ${a.maType}(${a.slow}) ${a.tf}`;
  return `${a.cond} ${a.value}`;
};

const fireAlert = (a, detail) => {
  a.counter = (a.counter || 0) + 1;
  if (a.counter >= a.limit) a.enabled = false;
  const msg = `${a.symbol} ${describeAlert(a)} (${detail})${a.comment ? " — " + a.comment : ""}`;
  log(`alert triggered: ${msg}`, "Alerts");
  if (a.action === "Sound") beep();
  state.popups.unshift({ id: Math.random(), time: Date.now(), msg });
};

const checkAlerts = () => {
  let fired = false;
  state.alerts.forEach((a) => {
    if (!a.enabled || !symbols[a.symbol]) return;
    const s = symbols[a.symbol];
    if (!a.kind || a.kind === "price") {
      const px = a.cond.startsWith("Bid") ? s.bid : s.ask;
      const hit = a.cond.endsWith(">") ? px > a.value : px < a.value;
      if (hit && !a.lastHit) { fireAlert(a, `price ${px.toFixed(s.digits)}`); fired = true; }
      a.lastHit = hit;
      return;
    }
    const { v, ref } = alertValue(a);
    if (v == null || ref == null) return;
    const side = v > ref ? 1 : -1;
    const prev = a.lastSide;
    a.lastSide = side;
    if (prev == null || prev === side) return;
    if (a.cross !== "crosses" && (a.cross === "crosses above") !== (side === 1)) return;
    fireAlert(a, a.kind === "rsi" ? `RSI ${v.toFixed(2)}` : `fast ${v.toFixed(s.digits)} / slow ${ref.toFixed(s.digits)}`);
    fired = true;
  });
  if (fired) persist();
};
export const saveAlert = (a) => update((st) => {
  const i = st.alerts.findIndex((x) => x.id === a.id);
  const v = { counter: 0, enabled: true, action: "Sound", comment: "", kind: "price", ...a, limit: Math.max(1, +a.limit || 1), lastHit: false, lastSide: null };
  if (v.kind === "price") v.value = +v.value;
  else { v.period = +v.period; v.level = +v.level; v.fast = +v.fast; v.slow = +v.slow; }
  if (i >= 0) st.alerts[i] = v; else st.alerts.push({ ...v, id: Date.now() });
  log(`alert ${i >= 0 ? "modified" : "created"}: ${v.symbol} ${describeAlert(v)}`, "Alerts");
});

const tick = () => {
  const now = Date.now();
  Object.values(symbols).forEach((s) => {
    if (s.live) {
      if (!s.liveQ) { s.dir = 0; return; }
      const q = s.liveQ;
      s.liveQ = null;
      return applyQuote(s, q.bid, q.ask, now);
    }
    if (s.snap != null) {
      const bid = s.snap;
      s.snap = null;
      return applyQuote(s, bid, bid + s.spread * s.point, now);
    }
    if (s.closed || Math.random() < 0.35) { s.dir = 0; return; }
    // anchored symbols only jitter a few points around the last real quote
    const bid = s.anchored
      ? s.bid + s.bid * gauss() * s.vol * 0.15 + (s.base - s.bid) * 0.3
      : s.bid + s.bid * (gauss() * s.vol) + (s.base - s.bid) * 0.0005;
    const spread = Math.max(1, Math.round(s.spread * (0.8 + Math.random() * 0.4)));
    applyQuote(s, bid, round(bid, s.digits) + spread * s.point, now);
  });
  checkTriggers();
  checkAlerts();
  emit();
};

setInterval(tick, TICK_MS);
log("'51842907': authorized on WebTrade-Demo", "Network");
log("'51842907': terminal synchronized with WebTrade-Demo: 0 positions, 0 orders, 24 symbols", "Network");
log("automated trading is enabled", "Terminal");
log("Web Terminal x64 build 4410 started for WebTrade Ltd.", "Terminal");
